import type { RegionCode } from '@/types'
import { getClientForRegion } from '@/api'
import type { LocationScope, ReportSelectOption, RfidReportFilters } from './reports.service'
import { reportsService } from './reports.service'

export type ValidationStatus = 'Match' | 'No Match' | 'Short Receipt' | 'Excess Received'

export type ValidationReportRow = {
  rr_no: string
  draft_tag: string | null
  actual_tag: string | null
  status: ValidationStatus
  sap_document: string | null
  id?: number | string | null
  rfid_code?: string | null
  warehouse?: string | null
  plant_name?: string | null
  customer_location?: string | null
  customer_name?: string | null
  document?: string | null
  in_transit?: string | null
  is_virtual?: string | null
  scan_status?: string | null
  item_no?: string | null
  transfer_type?: string | null
  remarks?: string | null
  created_at?: string | null
  updated_at?: string | null
  created_by?: string | null
  updated_by?: string | null
  ref_doc_num?: string | null
  material_code?: string | null
  material_desc?: string | null
  sender_code?: string | null
  sender_name?: string | null
  receiver_code?: string | null
  receiver_name?: string | null
  material_group_code?: string | null
  material_group_name?: string | null
  kit_no?: string | null
  region?: RegionCode
}

export type ValidationReportSummary = {
  match: number
  no_match: number
  short_receipt: number
  excess_received: number
}

export type ValidationReportFilters = RfidReportFilters

/** CUSTOUT draft validation is India-only (GCC has no draft concept). */
const VALIDATION_REGION: RegionCode = 'IN'

function filenameFromDisposition(header?: string): string | null {
  if (!header) return null
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim())
    } catch {
      return utf[1].trim()
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || null
}

async function readBlobErrorMessage(blob: Blob): Promise<string | null> {
  if (!blob || blob.type.includes('sheet') || blob.type.includes('octet-stream')) return null
  try {
    const text = await blob.text()
    const json = JSON.parse(text) as {
      message?: string
      error?: string
      errors?: Array<{ message?: string } | string>
    }
    if (typeof json.message === 'string') return json.message
    if (typeof json.error === 'string') return json.error
    if (Array.isArray(json.errors) && json.errors.length) {
      return json.errors
        .map((e) => (typeof e === 'string' ? e : e.message || ''))
        .filter(Boolean)
        .join('; ')
    }
  } catch {
    /* not JSON */
  }
  return null
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function cleanFilters(filters: ValidationReportFilters): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value == null) continue
    if (typeof value === 'string' && !value.trim()) continue
    if (Array.isArray(value) && value.length === 0) continue
    body[key] = Array.isArray(value) ? value : typeof value === 'string' ? value.trim() : value
  }
  return body
}

export const validationReportService = {
  /** Options still come from India only for this module. */
  async getOptions() {
    const client = getClientForRegion(VALIDATION_REGION)
    const { data } = await client.get<{
      success: boolean
      data: Awaited<ReturnType<typeof reportsService.getOptions>>
    }>('/rfid_report/options')
    return data.data
  },

  async preview(filters: ValidationReportFilters): Promise<{
    data: ValidationReportRow[]
    total: number
    previewLimit: number
    summary: ValidationReportSummary
  }> {
    const body = cleanFilters(filters)
    const client = getClientForRegion(VALIDATION_REGION)
    const { data } = await client.post<{
      success: boolean
      data: ValidationReportRow[]
      total: number
      previewLimit: number
      summary?: ValidationReportSummary
    }>('/rfid_validation_report/preview', body)

    return {
      data: (data.data ?? []).map((row) => ({ ...row, region: VALIDATION_REGION })),
      total: data.total ?? 0,
      previewLimit: data.previewLimit ?? 50,
      summary: data.summary ?? {
        match: 0,
        no_match: 0,
        short_receipt: 0,
        excess_received: 0,
      },
    }
  },

  async download(filters: ValidationReportFilters): Promise<{ filename: string }> {
    const body = cleanFilters(filters)
    const client = getClientForRegion(VALIDATION_REGION)

    try {
      const res = await client.post('/rfid_validation_report', body, {
        responseType: 'blob',
        timeout: 180_000,
      })

      const blob = res.data as Blob
      const errMsg = await readBlobErrorMessage(blob)
      if (errMsg) {
        throw { message: errMsg, statusCode: res.status }
      }

      const filename =
        filenameFromDisposition(res.headers['content-disposition'] as string | undefined) ||
        `rfid_validation_report_IN_${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:T]/g, '_')}.xlsx`

      triggerDownload(blob, filename)
      return { filename }
    } catch (err: unknown) {
      const e = err as {
        message?: string
        statusCode?: number
        response?: { data?: Blob; status?: number }
      }
      if (e.response?.data instanceof Blob) {
        const msg = await readBlobErrorMessage(e.response.data)
        if (msg) throw { message: msg, statusCode: e.response.status || e.statusCode }
      }
      throw err
    }
  },
}

export type { LocationScope, ReportSelectOption }
